// @ts-check
// Regras puras da grade de 12 colunas dos painéis: tamanhos por tipo, limites e empurrar para baixo ao colidir.

/**
 * @typedef {{ gridRow: number, gridColumn: number, rowSpan: number, columnSpan: number }} GridRectangle
 * @typedef {GridRectangle & { key: string }} GridItem
 */

export const GRID_COLUMN_COUNT = 12;
export const MAX_ROW_SPAN = 24;
export const GRID_ROW_HEIGHT = 64;
export const GRID_GAP = 8;

/** Tamanho padrão (colunas x linhas) de cada tipo, do contrato. */
export const DEFAULT_WIDGET_SIZES = {
  KPI: { columnSpan: 3, rowSpan: 2 },
  AGGREGATE: { columnSpan: 3, rowSpan: 2 },
  BAR: { columnSpan: 6, rowSpan: 6 },
  LINE: { columnSpan: 6, rowSpan: 6 },
  PIE: { columnSpan: 6, rowSpan: 6 },
  TABLE: { columnSpan: 12, rowSpan: 6 },
  TEXT: { columnSpan: 6, rowSpan: 3 },
  IFRAME: { columnSpan: 6, rowSpan: 6 },
  STAT_GROUP: { columnSpan: 12, rowSpan: 2 },
  BAR_LIST: { columnSpan: 6, rowSpan: 7 },
  DISTRIBUTION: { columnSpan: 6, rowSpan: 6 },
  FUNNEL_FLOW: { columnSpan: 12, rowSpan: 8 },
  STAGE_FLOW: { columnSpan: 12, rowSpan: 5 },
  UPCOMING_TABLE: { columnSpan: 12, rowSpan: 7 },
  GEO_MAP: { columnSpan: 12, rowSpan: 11 },
};

/** Menor tamanho em que o conteúdo de cada tipo ainda é legível. */
export const MINIMUM_WIDGET_SIZES = {
  KPI: { columnSpan: 2, rowSpan: 2 },
  AGGREGATE: { columnSpan: 2, rowSpan: 2 },
  BAR: { columnSpan: 3, rowSpan: 3 },
  LINE: { columnSpan: 3, rowSpan: 3 },
  PIE: { columnSpan: 3, rowSpan: 3 },
  TABLE: { columnSpan: 4, rowSpan: 3 },
  TEXT: { columnSpan: 2, rowSpan: 1 },
  IFRAME: { columnSpan: 4, rowSpan: 4 },
  STAT_GROUP: { columnSpan: 3, rowSpan: 2 },
  BAR_LIST: { columnSpan: 3, rowSpan: 3 },
  DISTRIBUTION: { columnSpan: 3, rowSpan: 3 },
  FUNNEL_FLOW: { columnSpan: 6, rowSpan: 5 },
  STAGE_FLOW: { columnSpan: 4, rowSpan: 4 },
  UPCOMING_TABLE: { columnSpan: 4, rowSpan: 3 },
  GEO_MAP: { columnSpan: 6, rowSpan: 7 },
};

/**
 * @param {GridRectangle} first
 * @param {GridRectangle} second
 */
export function doRectanglesOverlap(first, second) {
  return (
    first.gridColumn < second.gridColumn + second.columnSpan &&
    second.gridColumn < first.gridColumn + first.columnSpan &&
    first.gridRow < second.gridRow + second.rowSpan &&
    second.gridRow < first.gridRow + first.rowSpan
  );
}

/**
 * Mantém o retângulo dentro da grade e acima do tamanho mínimo do tipo.
 * @param {GridRectangle} rectangle
 * @param {{ columnSpan: number, rowSpan: number }} [minimum]
 * @returns {GridRectangle}
 */
export function clampRectangle(rectangle, minimum = { columnSpan: 1, rowSpan: 1 }) {
  const columnSpan = Math.max(minimum.columnSpan, Math.min(GRID_COLUMN_COUNT, Math.round(rectangle.columnSpan)));
  const rowSpan = Math.max(minimum.rowSpan, Math.min(MAX_ROW_SPAN, Math.round(rectangle.rowSpan)));
  return {
    gridColumn: Math.max(0, Math.min(GRID_COLUMN_COUNT - columnSpan, Math.round(rectangle.gridColumn))),
    gridRow: Math.max(0, Math.round(rectangle.gridRow)),
    columnSpan,
    rowSpan,
  };
}

/**
 * Coloca um item no retângulo pedido e empurra para baixo, em cascata, os que passarem a colidir.
 * Os demais itens não sobem: a posição escolhida por quem montou o painel é preservada.
 * @template {GridItem} TItem
 * @param {TItem[]} items
 * @param {string} key
 * @param {GridRectangle} rectangle
 * @returns {TItem[]}
 */
export function placeItem(items, key, rectangle) {
  const target = items.find((item) => item.key === key);
  if (!target) return items;
  const movedTarget = { ...target, ...rectangle };
  /** @type {TItem[]} */
  const placed = [movedTarget];
  const others = items
    .filter((item) => item.key !== key)
    .map((item) => ({ ...item }))
    .sort((first, second) => first.gridRow - second.gridRow || first.gridColumn - second.gridColumn);
  for (const item of others) {
    let collision = placed.find((other) => doRectanglesOverlap(item, other));
    while (collision) {
      item.gridRow = collision.gridRow + collision.rowSpan;
      collision = placed.find((other) => doRectanglesOverlap(item, other));
    }
    placed.push(item);
  }
  const byKey = new Map(placed.map((item) => [item.key, item]));
  return items.map((item) => /** @type {TItem} */ (byKey.get(item.key)));
}

/**
 * Passo de uma coluna e de uma linha em pixels para arrastar e redimensionar, com o espaço real entre os cartões
 * (12 px nas páginas de relatório, 8 px nas demais): com o espaço fixo, o encaixe escorregava uma linha a cada cinco.
 * @param {number} gridWidth @param {number} [gap]
 */
export function gridSteps(gridWidth, gap = GRID_GAP) {
  const columnWidth = (gridWidth - gap * (GRID_COLUMN_COUNT - 1)) / GRID_COLUMN_COUNT;
  return { stepX: columnWidth + gap, stepY: GRID_ROW_HEIGHT + gap };
}

/**
 * Espaço entre os cartões que a página aplica de fato (lido do estilo da grade).
 * @param {Element | null | undefined} grid
 */
export function readGridGap(grid) {
  if (!grid || typeof getComputedStyle !== 'function') return GRID_GAP;
  const gap = Number.parseFloat(getComputedStyle(grid).rowGap);
  return Number.isFinite(gap) ? gap : GRID_GAP;
}

/**
 * Primeira linha livre abaixo de todos os itens (onde entram widgets novos e duplicados).
 * @param {GridRectangle[]} items
 */
export function getBottomRow(items) {
  return items.reduce((bottom, item) => Math.max(bottom, item.gridRow + item.rowSpan), 0);
}

/**
 * Ordem de leitura (linha e coluna), usada na coluna única do celular.
 * @template {GridRectangle} TItem
 * @param {TItem[]} items
 */
export function sortByReadingOrder(items) {
  return [...items].sort((first, second) => first.gridRow - second.gridRow || first.gridColumn - second.gridColumn);
}
